import {useEffect, useRef, useState} from 'react';
import {Input} from '@/components/ui/input';
export interface DateFieldProps {
  value: string | null;
  onValueChange: (value: string | null) => void;
  id?: string;
  disabled?: boolean;
  onBlur?: () => void;
}
export function DateField({value, onValueChange, id, disabled, onBlur}: DateFieldProps) {
  const [parts, setParts] = useState(() => (value ?? '').split('-'));
  const month = useRef<HTMLInputElement>(null);
  const day = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setParts((value ?? '').split('-'));
  }, [value]);
  function update(index: number, text: string) {
    const digits = text.replace(/\D/g, '').slice(0, index === 0 ? 4 : 2);
    const next = [...parts];
    next[index] = digits;
    if (digits.length === (index === 0 ? 4 : 2)) {
      const today = new Date();
      const year = Number(next[0]);
      const maxMonth = year === today.getFullYear() ? today.getMonth() + 1 : 12;
      const limit =
        index === 0
          ? today.getFullYear()
          : index === 1
            ? maxMonth
            : new Date(year || today.getFullYear(), Number(next[1]) || 1, 0).getDate();
      next[index] = String(Math.max(index === 0 ? 1900 : 1, Math.min(limit, Number(digits)))).padStart(
        index === 0 ? 4 : 2,
        '0',
      );
      if (index === 0) month.current?.focus();
      if (index === 1) day.current?.focus();
    }
    setParts(next);
    const complete: string[] = [];
    for (let i = 0; i < 3; i++) {
      if (next[i]?.length !== (i === 0 ? 4 : 2)) break;
      complete.push(next[i]);
    }
    const nextValue = complete.length ? complete.join('-') : null;
    if (nextValue !== (value ?? null)) onValueChange(nextValue);
  }
  return (
    <div id={id} role="group" aria-label="Date of birth" className="flex w-full items-center">
      <Input
        aria-label="Birth year"
        placeholder="YYYY"
        inputMode="numeric"
        maxLength={4}
        value={parts[0] ?? ''}
        className="min-w-0 flex-1 rounded-r-none text-center"
        disabled={disabled}
        onBlur={onBlur}
        onChange={(event) => update(0, event.target.value)}
      />
      <Input
        ref={month}
        aria-label="Birth month"
        placeholder="MM"
        inputMode="numeric"
        maxLength={2}
        value={parts[1] ?? ''}
        className="min-w-0 flex-1 rounded-none text-center"
        disabled={disabled}
        onBlur={onBlur}
        onChange={(event) => update(1, event.target.value)}
      />
      <Input
        ref={day}
        aria-label="Birth day (optional)"
        placeholder="DD"
        inputMode="numeric"
        maxLength={2}
        value={parts[2] ?? ''}
        className="min-w-0 flex-1 rounded-l-none text-center"
        disabled={disabled}
        onBlur={onBlur}
        onChange={(event) => update(2, event.target.value)}
      />
    </div>
  );
}
